import { Component, OnInit, OnChanges, Input, EventEmitter, Output } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { formatDistance } from 'date-fns';
import { ORDER_MEDICINE_VIEW_ROLES } from 'src/core/config/portal-route-roles';
import { MedicineService } from 'src/core/services/medicine.service';
import { OrderMedicineService } from 'src/core/services/order-medicine.service';
import { DosageUnit, MedicineDosagePackageType, PackageType } from 'src/interfaces/medicine';
import { CreateOrderMedicinePayload } from 'src/interfaces/order-medicine';
import { isBlankSageItemCode, readSageItemCode, SAGE_ITEM_CODE_MISSING_WARNING, sageItemCodeForApi, withSavedSageItemCode } from 'src/core/utils/sage-item-code';

@Component({
    selector: 'core-medicine-dosage-package-type-list',
    templateUrl: './medicine-dosage-package-type-list.component.html',
    styleUrls: ['./medicine-dosage-package-type-list.component.scss'],
    standalone: false
})
export class MedicineDosagePackageTypeListComponent implements OnInit, OnChanges {

  displayedColumns: string[] = [
    'label_name',
    'type',
    'quantity',
    'units_per_pack',
    'min_order_qty',
    'price',
    'sage_item_code',
    'created',
    'actions'
  ]

  @Input() medicineDosagePackageTypes: MedicineDosagePackageType[];
  @Input() medicineId: number;
  @Input() medicineDosageTypeId: number;
  @Input() packageTypes: PackageType[];
  @Output() onUpdated = new EventEmitter<boolean>();
  @Input() dosageUnit: DosageUnit | null = null;

  readonly orderMedicineViewRoles = ORDER_MEDICINE_VIEW_ROLES;
  readonly sageItemCodeMissingWarning = SAGE_ITEM_CODE_MISSING_WARNING;

  // pagination state
  page: number = 1;
  limit: number = 10;
  total: number = 0;

  orderMedicinePackageTypeIds = new Set<number>();
  isLoadingOrderMedicines = false;
  addingPackageTypeId: number | null = null;
  /** Codes saved in this screen. Kept when a list reload comes back without them. */
  private savedSageCodes = new Map<number, string>();

  constructor(
    private _medicineService: MedicineService,
    private _orderMedicineService: OrderMedicineService
  ) { }

  ngOnInit(): void {
    this.total = this._medicineService.getTotalNumberOfMedicineDosagePackageTypes();
    this.loadExistingOrderPackageTypeIds();
  }

  ngOnChanges(): void {
    this.applySavedSageCode();
  }

  async edit(medicineDosagePackageType: MedicineDosagePackageType) {
    const result = await this._medicineService
      .openMedicineDosagePackageTypeEditDialog(
        "Edit package type",
        "Edit package type",
        this.medicineId,
        this.medicineDosageTypeId,
        this.packageTypes,
        medicineDosagePackageType
      );

    if (!result || typeof result !== 'object') return;

    this.savedSageCodes.set(result.id, sageItemCodeForApi(result.sageItemCode));
    this.applySavedSageCode();
    this.onUpdated.emit(true);
  }

  async delete(medicineDosagePackageType: MedicineDosagePackageType) {

    const success: boolean = await this._medicineService
      .deleteMedicineDosagePackageType(
        this.medicineId,
        this.medicineDosageTypeId,
        medicineDosagePackageType.id);

    if (success)
      this.medicineDosagePackageTypes = this.medicineDosagePackageTypes.filter(_medicineDosagePackageType => _medicineDosagePackageType.id !== medicineDosagePackageType.id);

  }

  isMissingSageItemCode(medicineDosagePackageType: MedicineDosagePackageType): boolean {
    return isBlankSageItemCode(readSageItemCode(medicineDosagePackageType));
  }

  sageItemCodeLabel(medicineDosagePackageType: MedicineDosagePackageType): string {
    return readSageItemCode(medicineDosagePackageType);
  }

  isInOrderSystem(medicineDosagePackageType: MedicineDosagePackageType): boolean {
    return this.orderMedicinePackageTypeIds.has(medicineDosagePackageType.id);
  }

  isAddToOrderDisabled(medicineDosagePackageType: MedicineDosagePackageType): boolean {
    return this.isLoadingOrderMedicines
      || this.addingPackageTypeId !== null
      || this.isInOrderSystem(medicineDosagePackageType);
  }

  addToOrderTooltip(medicineDosagePackageType: MedicineDosagePackageType): string {
    if (this.isInOrderSystem(medicineDosagePackageType)) {
      return 'Already added to the order system';
    }
    return 'Add this package type to the order system';
  }

  async addToOrderSystem(medicineDosagePackageType: MedicineDosagePackageType) {
    if (this.isAddToOrderDisabled(medicineDosagePackageType)) {
      return;
    }

    const payload: CreateOrderMedicinePayload = {
      medicine_dosage_package_type_id: medicineDosagePackageType.id,
      dosage_package_type_id: medicineDosagePackageType.package_type_id,
      medicine_id: this.medicineId
    };

    this.addingPackageTypeId = medicineDosagePackageType.id;
    try {
      const result = await this._orderMedicineService.createOrderMedicine(payload);
      if (result) {
        this.orderMedicinePackageTypeIds.add(medicineDosagePackageType.id);
      }
    } finally {
      this.addingPackageTypeId = null;
    }
  }

  getCreationTime(medicineDosagePackageType: MedicineDosagePackageType) {
    return formatDistance(new Date(medicineDosagePackageType.created_at), new Date(), { addSuffix: true });
  }

  async onPageChange(event: PageEvent) {
    this.page = event.pageIndex + 1;
    this.limit = event.pageSize;
    await this.loadPage(this.page, this.limit);
  }

  async prevPage() {
    if (this.page <= 1) return;
    this.page = this.page - 1;
    await this.loadPage(this.page, this.limit);
  }

  async nextPage() {
    const lastPage = this.total > 0 ? Math.ceil(this.total / this.limit) : 1;
    if (this.page >= lastPage) return;
    this.page = this.page + 1;
    await this.loadPage(this.page, this.limit);
  }

  private async loadPage(page: number, limit: number) {
    const results: MedicineDosagePackageType[] = await this._medicineService
      .getMedicineDosagePackageTypes(
        this.medicineId,
        this.medicineDosageTypeId,
        page,
        limit,
        ''
      );
    this.medicineDosagePackageTypes = results;
    this.applySavedSageCode();
    this.total = this._medicineService.getTotalNumberOfMedicineDosagePackageTypes();
  }

  /** Put codes just saved back onto the rows when the reloaded list leaves them blank. */
  private applySavedSageCode(): void {
    const packs = this.medicineDosagePackageTypes;
    if (!packs) return;
    // Remember a code that arrived on the row (from the API or a create) so the
    // next reload cannot wipe it. A code saved in this screen stays as saved.
    for (const pack of packs) {
      if (this.savedSageCodes.has(pack.id)) continue;
      const code = readSageItemCode(pack);
      if (code) this.savedSageCodes.set(pack.id, code);
    }
    const next = withSavedSageItemCode(packs, this.savedSageCodes);
    if (next !== this.medicineDosagePackageTypes) {
      this.medicineDosagePackageTypes = next;
    }
  }

  private async loadExistingOrderPackageTypeIds() {
    this.isLoadingOrderMedicines = true;
    const ids = new Set<number>();
    let page = 1;
    const limit = 100;

    try {
      while (true) {
        const results = await this._orderMedicineService.getOrderMedicines(page, limit);
        for (const item of results) {
          ids.add(item.medicine_dosage_package_type_id);
        }
        if (results.length < limit) {
          break;
        }
        page++;
      }
      this.orderMedicinePackageTypeIds = ids;
    } finally {
      this.isLoadingOrderMedicines = false;
    }
  }

}

// computed helpers
export interface MedicineDosagePackageTypeListComponent {
  readonly lastPage: number;
}

Object.defineProperty(MedicineDosagePackageTypeListComponent.prototype, 'lastPage', {
  get: function(this: MedicineDosagePackageTypeListComponent) {
    const total = (this as any).total as number;
    const limit = (this as any).limit as number;
    return total > 0 ? Math.ceil(total / limit) : 1;
  },
  enumerable: true,
  configurable: true
});
